/**
 * The shell's palette providers (S6). Each is (ctx) -> palette entries (paletteMatch.js) and
 * reads what the shell already has; nothing is defined here that the sidebar does not offer.
 *
 *   navigate  the sidebar's modules, gated as the sidebar gates them (navEntriesFor)
 *   shell     sidebar rail / expanded (useSidebarMode), pin / unpin the Sales flyout
 *             (shell.flyout.pinned since G0, salesPin.js; offered where the pinned column can show)
 */
const pinReason = (minWidth) =>
  `Pinning needs the rail sidebar on a screen at least ${minWidth || 1181} px wide`;

const onPath = (pathname, path) => pathname === path || String(pathname || '').startsWith(`${path}/`);

/** ctx = { navEntries: [{ id, path, label, section }], navigate, pathname } */
export function navigateProvider({ navEntries, navigate, pathname }) {
  // The most specific module the URL is on (/leads/oversight is Lead Oversight, not Leads).
  const current = (navEntries || [])
    .filter((n) => onPath(pathname, n.path))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return (navEntries || []).map((n) => ({
    id: `nav:${n.id}`,
    group: 'Navigate',
    label: `Go to ${n.label}`,
    hint: n.section || '',
    available: current && current.id === n.id ? `You are on ${n.label}` : true,
    run: () => navigate(n.path)
  }));
}

/** ctx = { sidebar: { mode, toggle }, pin: { canPin, pinned, setPinned, minWidth } } */
export function shellProvider({ sidebar, pin }) {
  const entries = [];
  if (sidebar) {
    entries.push(
      {
        id: 'shell:sidebar-rail',
        group: 'Shell',
        label: 'Sidebar: rail',
        hint: 'Icons only',
        available: sidebar.mode === 'rail' ? 'The sidebar is already a rail' : true,
        run: () => sidebar.toggle()
      },
      {
        id: 'shell:sidebar-expanded',
        group: 'Shell',
        label: 'Sidebar: expanded',
        hint: 'Icons and names',
        available: sidebar.mode === 'expanded' ? 'The sidebar is already expanded' : true,
        run: () => sidebar.toggle()
      }
    );
  }
  if (pin) {
    entries.push({
      id: 'shell:pin',
      group: 'Shell',
      label: pin.pinned ? 'Unpin the Sales menu' : 'Pin the Sales menu',
      hint: '',
      // Unpinning is always possible; pinning only where the pinned column shows (S4b).
      available: pin.pinned || pin.canPin ? true : pinReason(pin.minWidth),
      run: () => pin.setPinned(!pin.pinned)
    });
  }
  return entries;
}

export const SHELL_PROVIDERS = [navigateProvider, shellProvider];
